import type { AccessPolicy, AccessPolicyUpdate } from "./_shared"
import type { FairUseTier } from "./fair-use"

import { api } from "@/lib/api-client"

// Model Management
export interface ModelStatus {
  id: string
  displayName: string
  description: string
  deployment: string
  enabled: boolean
  requiredModules: string[]
  blockedModules: string[]
  feature_flags: Record<string, unknown>
  access_configured?: boolean
  access_overridden?: boolean
  effort_access_configured?: boolean
  service_name?: string
  service_type?: string
  /** Specialized entries only: the kind of deployment this registry row is. */
  type?: "realtime" | "image" | "video" | "audio"
  /** Image entries only: the Azure image *family* id (e.g. `gpt-image-2.5-flare`). */
  model?: string
  /** Embedding entries only: vector size. */
  dimensions?: number
  /**
   * Fair-use plan allowlist (#3087, spec R2): the plans this model may be
   * chosen by. `null` / absent means EVERY plan — a model is never restricted
   * to nobody, so the backend removes the key rather than storing `[]`.
   */
  plans?: FairUseTier[] | null
}

export async function getModels(): Promise<ModelStatus[]> {
  const response = await api.get("/admin/models")
  return response.data || response
}

/**
 * Every `model_type=specialized` registry entry (image, realtime, video,
 * audio), enabled or not — the Specialized tab of the Models card.
 */
export async function getSpecializedModels(): Promise<ModelStatus[]> {
  const response = await api.get("/admin/specialized")
  return response.data || response
}

export async function updateModelStatus(
  modelId: string,
  enabled: boolean
): Promise<{
  success: boolean
  model_id: string
  enabled: boolean
  message: string
}> {
  const response = await api.put(`/admin/models/${modelId}/status`, { enabled })
  const data = response.data || response

  // Check if the operation actually succeeded
  if (data.success === false) {
    throw new Error(data.message || `Failed to update model ${modelId} status`)
  }

  return data
}

export async function getModelStatuses(): Promise<Record<string, { enabled: boolean }>> {
  const response = await api.get("/admin/models/status")
  return response.data || response
}

export async function updateModelsStatusBatch(updates: Record<string, boolean>): Promise<{
  success: boolean
  updated_models: string[]
  message: string
}> {
  const response = await api.put("/admin/models/status/batch", { updates })
  const data = response.data || response

  // Check if the operation actually succeeded
  if (data.success === false) {
    throw new Error(data.message || "Failed to batch update model statuses")
  }

  return data
}

// Model Create Types and Function
export type ModelType = "chat" | "embedding" | "specialized"

export interface CreateModelRequest {
  id: string
  display_name: string
  description?: string
  model_type: ModelType
  service_name: string
  deployment: string
  service_type?: "azure" | "openai"
  enabled?: boolean
  // Chat model specific
  input_token_limit?: number
  output_token_limit?: number
  feature_flags?: {
    supports_images?: boolean
    supports_streaming?: boolean
    supports_temperature?: boolean
    is_reasoning_model?: boolean
    supports_thinking_streaming?: boolean
    supports_system_message?: boolean
    supports_max_completion_tokens?: boolean
    supports_reasoning_efforts?: boolean
    supports_tool_calls?: boolean
    supports_structured_outputs?: boolean
  }
  disclaimers?: string[]
  // Embedding model specific
  dimensions?: number
  // Specialized specific
  type?: "realtime" | "image" | "video" | "audio"
  api_version?: string
  /**
   * Image-family id for `model_type=specialized`, `type=image` entries
   * (e.g. `gpt-image-2.5-flare`). Omitted means "infer from the deployment
   * name" server-side.
   */
  model?: string
  /** Chat models only: the fair-use plan allowlist (omit / `[]` = every plan). */
  plans?: FairUseTier[]
}

export interface CreateModelResponse {
  success: boolean
  model_id: string
  model?: ModelStatus & {
    model_type: ModelType
    service_name: string
    service_type: string
    input_token_limit?: number
    output_token_limit?: number
    dimensions?: number
    type?: string
    api_version?: string
    created_at?: string
    updated_at?: string
    source?: string
  }
  message: string
}

export async function createModel(config: CreateModelRequest): Promise<CreateModelResponse> {
  const response = await api.post("/admin/models", config)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || "Failed to create model")
  }

  return data
}

export interface DeleteModelResponse {
  success: boolean
  model_id: string
  message: string
}

export async function deleteModel(modelId: string): Promise<DeleteModelResponse> {
  const response = await api.delete(`/admin/models/${modelId}`)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to delete model ${modelId}`)
  }

  return data
}

export async function getModelAccessPolicy(modelId: string): Promise<AccessPolicy> {
  const response = await api.get(`/admin/models/${modelId}/access`)
  const data = response.data || response
  return data.access || {}
}

export async function getModelBaseAccessPolicy(): Promise<AccessPolicy> {
  const response = await api.get("/admin/models/base/access")
  const data = response.data || response
  return data.access || {}
}

export async function updateModelAccessPolicy(
  modelId: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/models/${modelId}/access`, policy)
  const data = response.data || response
  return data.access || {}
}

export async function updateModelBaseAccessPolicy(
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put("/admin/models/base/access", policy)
  const data = response.data || response
  return data.access || {}
}

export async function clearModelAccessPolicy(modelId: string): Promise<AccessPolicy> {
  const response = await api.put(`/admin/models/${modelId}/access`, {})
  const data = response.data || response
  return data.access || {}
}

// =============================================================================
// Image model configuration API (#3038)
// =============================================================================

/** Capability sheet for one Azure OpenAI image model family. */
export interface ImageModelFamily {
  id: string
  label: string
  tier: "fast" | "quality"
  qualities: string[]
  default_quality: string
  sizes: string[]
  supports_transparent: boolean
}

/** One registry entry of `model_type=specialized`, `type=image`. */
export interface ImageModelEntry {
  id: string
  display_name: string
  description: string
  deployment: string
  enabled: boolean
  family: string
  family_label: string
  tier: "fast" | "quality"
}

/**
 * Surface configuration plus the catalog needed to render it.
 *
 * The stored ids may be empty; `effective` then carries what the backend
 * resolves them to, so the UI can show "Automatic: <name>" instead of blank.
 */
export interface ImageModelConfig {
  explorer_model_id: string
  studio_default_model_id: string
  studio_user_selectable: boolean
  effective: {
    explorer_model_id: string
    studio_default_model_id: string
  }
  models: ImageModelEntry[]
  families: ImageModelFamily[]
}

export type ImageModelConfigPatch = Partial<
  Pick<ImageModelConfig, "explorer_model_id" | "studio_default_model_id" | "studio_user_selectable">
>

export async function getImageModelConfig(): Promise<ImageModelConfig> {
  const response = await api.get("/admin/image/models")
  return response.data || response
}

export async function updateImageModelConfig(
  patch: ImageModelConfigPatch
): Promise<ImageModelConfig & { success: boolean; message?: string }> {
  const response = await api.put("/admin/image/models", patch)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || "Failed to update image model configuration")
  }

  return data
}

// Per-effort access policies (#2667): gate individual reasoning effort levels
// (e.g. xhigh/max) per user group, globally and per model.
export const EFFORT_LEVELS = ["none", "minimal", "low", "medium", "high", "xhigh", "max"] as const

export type EffortLevel = (typeof EFFORT_LEVELS)[number]

/**
 * `disabled` is a standalone quick-toggle — hard-off for everyone regardless
 * of the ACL fields — kept separate from `requires`/`block`/`unrestricted` so
 * toggling it back off preserves any previously configured allow/block list.
 */
export type EffortPolicy = AccessPolicy & { disabled?: boolean }

export type EffortAccessMap = Partial<Record<EffortLevel, EffortPolicy>>

export async function getModelBaseEffortAccess(): Promise<EffortAccessMap> {
  const response = await api.get("/admin/models/base/effort-access")
  const data = response.data || response
  return data.effort_access || {}
}

export async function updateModelBaseEffortAccess(map: EffortAccessMap): Promise<EffortAccessMap> {
  const response = await api.put("/admin/models/base/effort-access", map)
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || "Failed to update model base effort access")
  }
  return data.effort_access || {}
}

export async function getModelEffortAccess(modelId: string): Promise<EffortAccessMap> {
  const response = await api.get(`/admin/models/${modelId}/effort-access`)
  const data = response.data || response
  return data.effort_access || {}
}

export async function updateModelEffortAccess(
  modelId: string,
  map: EffortAccessMap
): Promise<EffortAccessMap> {
  const response = await api.put(`/admin/models/${modelId}/effort-access`, map)
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || `Failed to update effort access for model ${modelId}`)
  }
  return data.effort_access || {}
}

// =============================================================================
// Fair-use plan restriction (#3087, spec R2)
// =============================================================================
//
// Which plans may pick a model. Server-side this is one optional `plans` key on
// the model's `system_models` override; `null` (and the absent key, and an
// empty list) all mean "every plan", so the UI has exactly ONE unrestricted
// state to render. Enforcement lives in `utils/model_access.py`; a restricted
// model is also filtered out of `/api/config`'s model list for other plans, so
// this is a commercial control, not a security boundary on its own.

/** `{model_id, plans}` — `plans: null` is the unrestricted state. */
export type ModelPlansResponse = {
  model_id: string
  plans: FairUseTier[] | null
}

export async function getModelPlans(modelId: string): Promise<ModelPlansResponse> {
  const response = await api.get(`/admin/models/${encodeURIComponent(modelId)}/plans`)
  // Read through `Partial`: the body comes off the wire, and an older or
  // half-answered response must resolve to "every plan", not to `undefined`.
  const data = (response.data || response) as Partial<ModelPlansResponse>
  return {
    model_id: data.model_id ?? modelId,
    plans: Array.isArray(data.plans) ? data.plans : null,
  }
}

/**
 * Replace a model's plan allowlist (whole-list PUT).
 *
 * Pass `null` — or an empty list, which is normalized to `null` here rather
 * than sent — to clear the restriction. An unknown plan id answers 400 with
 * `{error}` and writes nothing.
 */
export async function updateModelPlans(
  modelId: string,
  plans: FairUseTier[] | null
): Promise<ModelPlansResponse> {
  const body = { plans: plans && plans.length > 0 ? plans : null }
  const response = await api.put(`/admin/models/${encodeURIComponent(modelId)}/plans`, body)
  const data = (response.data || response) as Partial<ModelPlansResponse> & {
    success?: boolean
    message?: string
  }
  if (data.success === false) {
    throw new Error(data.message || `Failed to update plans for model ${modelId}`)
  }
  return {
    model_id: data.model_id ?? modelId,
    plans: Array.isArray(data.plans) ? data.plans : null,
  }
}

// Budget model mapping (HERMES Fas 6): the model hosted agents downgrade to
// when their budget is exceeded and `on_exceeded` is "budget_model". Stored
// in system_models_base.budget_model_id; a non-empty id must reference an
// enabled configured chat model (validated server-side).
export async function getBudgetModel(): Promise<string> {
  const response = await api.get("/admin/models/budget")
  const data = response.data || response
  return typeof data.budget_model_id === "string" ? data.budget_model_id : ""
}

export async function updateBudgetModel(budgetModelId: string): Promise<string> {
  const response = await api.put("/admin/models/budget", { budget_model_id: budgetModelId })
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to update budget model mapping")
  }
  return data.budget_model_id ?? ""
}
